import React, { useCallback, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import {
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY,
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT,
} from '@rover/components/src/callouts/CalloutBadge/CalloutBadge.constants';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import Calendar from '@rover/icons/svg/calendar.svg';
import CheckCircleBlue from '@rover/icons/svg/check-circle-blue.svg';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import useTheme from '@rover/kibble/themes/useTheme';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import ContactButton from '../../../components/ContactButton';
import { SitterProfileVersionOptions } from '../../constants';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { useRevampSelectedDates } from '../../hooks/useRevampSelectedDates';
import { useUpdateSelectedDates } from '../../hooks/useUpdateSelectedDates';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';
import AdditionalRatesSection from '../ServiceSection/AdditionalRatesSection';

import { SitterProfileSeeAllRatesClicked } from './analyticsEvents';
import RevampCancellationPolicy from './RevampCancellationPolicy';
import RevampDatesDropdown from './RevampDatesDropdown';
import { DateSelection } from './RevampDatesPicker';
import { CTAPropsCommon } from './RevampServicesCta';
import ServiceIconBadge from './ServiceIconBadge';
import { getRangeEndDate, getSitterSlugFromWebUrl, serviceIconToGraphic } from './utils';

type Props = Omit<CTAPropsCommon, 'bookingFeePercentage'> & {
  handleOpenSheet: () => void;
  isOpen: boolean;
  selectedDate: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
};

const RevampServicesCtaPriceDisplay = ({
  handleOpenSheet,
  isOpen,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  selectedDate,
  selectedEndDate,
  hasDateRange,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const theme = useTheme();
  const { contactUrl, showSittersRates } = useRevampProfileContext();
  const {
    sitterData,
    serviceType,
    selectedService,
    cancellationPolicy,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
    isContactUnavailable,
  } = useRevampCta();
  const { rate, price } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const currentUser = useCurrentUser();
  const location = useLocationContext();

  const updateSelectedDates = useUpdateSelectedDates();

  const datesRowRef = useRef<HTMLElement>(null);
  const [isDatesDropdownOpen, setIsDatesDropdownOpen] = useState(false);
  // The dropdown exempts its trigger from click-outside, so the row has to toggle.
  const handleDatesPress = useCallback(() => setIsDatesDropdownOpen((open) => !open), []);
  const handleDatesDropdownClose = useCallback(() => setIsDatesDropdownOpen(false), []);
  const handleDatesSave = useCallback(
    (selection: DateSelection) => updateSelectedDates(selection),
    [updateSelectedDates]
  );

  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { brightHorizonsBanner, isShowingBhBanner } = useRevampBrightHorizonsBanner(
    petType,
    petCount
  );

  const { formattedPrice, beforePriceText, afterPriceText, showBrightHorizonsCalloutBadge } =
    usePriceDisplayInfo({
      currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
      priceTransparencyLevel,
      locale,
      price: price ?? '',
      priceInCredits,
      shouldShowPriceInCredits,
      showFromText: !shouldShowPriceInCredits,
      unitAsSentence: rate?.unitAsSentence ?? '',
      isBHUser: isBrightHorizonsUser,
    });

  const {
    allowsLooseDates,
    allowsRecurring,
    looseDates,
    isRecurring,
    weekDays,
    hasDates,
    datesLabel: selectedDatesLabel,
  } = useRevampSelectedDates(locale, selectedDate, selectedEndDate, hasDateRange);
  const datesLabel = hasDates ? selectedDatesLabel : 'Add dates';

  const ServiceIcon = selectedService ? serviceIconToGraphic[selectedService.icon] : undefined;

  const serviceName = selectedService?.displayName;
  const serviceDescription = selectedService?.description;
  const serviceRowLabel = serviceName
    ? [`Change service, currently ${serviceName}`, serviceDescription].filter(Boolean).join('. ')
    : 'Select a service';

  const rowProps = {
    webAs: 'button' as const,
    alignItems: 'center' as const,
    gap: '4x' as const,
    px: '4x' as const,
    py: '4x' as const,
    width: '100%' as const,
    border: 'none',
    sx: { background: 'transparent', cursor: 'pointer', textAlign: 'left' },
  };

  return (
    <Flex data-testid="revamp-services-cta-price-display" flexDirection="column">
      {rate && price && !isSelectedServiceUnapproved && (
        <AdditionalRatesSection
          services={sitterData.services}
          petType={petType}
          petCount={petCount}
          showSittersRates={showSittersRates}
          isCtaModal
          isRevampVersion
          renderTrigger={(toggleModal) => (
            <Flex alignItems="baseline" flexWrap="wrap" gap="2x" mb="6x">
              <Flex
                webAs="button"
                flexWrap="wrap"
                alignItems="baseline"
                gap="1x"
                border="none"
                padding="0x"
                data-testid="revamp-services-cta-price-display-price"
                aria-label={`${beforePriceText} ${formattedPrice} ${afterPriceText}, see all rates`}
                onPress={() => {
                  emitAnalyticsEvent(
                    new SitterProfileSeeAllRatesClicked({
                      serviceSlug: serviceType ?? '',
                      pageVariant: SitterProfileVersionOptions.REVAMP,
                    })
                  );
                  toggleModal();
                }}
                sx={{ background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              >
                {beforePriceText && (
                  <Text size="200" textColor="secondary" fontWeight="regular" aria-hidden="true">
                    {beforePriceText}
                  </Text>
                )}
                <Heading
                  size="400"
                  mb="0x"
                  textColor="price"
                  webProps={{ as: 'span', responsive: false }}
                  aria-hidden="true"
                  sx={{ textDecoration: 'underline' }}
                >
                  {formattedPrice}
                </Heading>
                <Text size="200" textColor="secondary" fontWeight="regular" aria-hidden="true">
                  {afterPriceText}
                </Text>
              </Flex>
              {showBrightHorizonsCalloutBadge && (
                <Badge
                  {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY}
                  text={i18n._(BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT)}
                />
              )}
            </Flex>
          )}
        />
      )}

      {isSelectedServiceUnapproved && (
        <Box pb="4x">
          <Badge
            boxProps={{ testID: 'cta-coming-soon-badge', alignSelf: 'flex-start' }}
            text="Coming Soon"
            variant="default"
          />
        </Box>
      )}

      <Box position="relative">
        <Box borderRadius="8px" border={`1px solid ${theme.colors.separator.primary}`}>
          <Flex
            {...rowProps}
            data-testid="revamp-services-cta-price-display-service"
            onPress={handleOpenSheet}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={serviceRowLabel}
            borderBottom={`1px solid ${theme.colors.separator.primary}`}
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="4x" sx={{ minWidth: 0 }}>
              {ServiceIcon && (
                <ServiceIconBadge
                  icon={ServiceIcon}
                  serviceSlug={selectedService?.slug}
                  testID="revamp-services-cta-price-display-service-icon"
                />
              )}
              <Flex flexDirection="column" alignItems="flex-start" aria-hidden="true">
                <Flex alignItems="center" gap="2x">
                  <Text size="200" fontWeight="semibold">
                    {selectedService?.displayName ?? 'Select a service'}
                  </Text>
                  {selectedService && (
                    <CheckCircleBlue
                      width="20"
                      height="20"
                      aria-hidden="true"
                      data-testid="revamp-services-cta-price-display-service-check"
                    />
                  )}
                </Flex>
                {serviceDescription && (
                  <Text size="200" textColor="secondary">
                    {serviceDescription}
                  </Text>
                )}
              </Flex>
            </Flex>

            <Box
              width="24px"
              height="24px"
              flexShrink="0"
              aria-hidden="true"
              sx={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <DropdownSmall width="24" height="24" fill={theme.colors.text.primary} />
            </Box>
          </Flex>

          <Flex
            {...rowProps}
            ref={datesRowRef}
            data-testid="revamp-services-cta-price-display-dates"
            onPress={handleDatesPress}
            aria-haspopup="dialog"
            aria-expanded={isDatesDropdownOpen}
            aria-label={hasDates ? `Change dates, ${datesLabel}` : 'Add dates'}
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="4x" sx={{ minWidth: 0 }}>
              <ServiceIconBadge
                icon={Calendar}
                testID="revamp-services-cta-price-display-dates-icon"
                backgroundColor={theme.colors.neutral[200].toString()}
                iconColor={theme.colors.neutral[900].toString()}
              />
              <Flex flexDirection="column" alignItems="flex-start" aria-hidden="true">
                <Flex alignItems="center" gap="2x">
                  <Text size="200" fontWeight="semibold">
                    Dates
                  </Text>
                  {hasDates && (
                    <CheckCircleBlue
                      width="20"
                      height="20"
                      aria-hidden="true"
                      data-testid="revamp-services-cta-price-display-dates-check"
                    />
                  )}
                </Flex>
                <Text size="200" textColor="secondary">
                  {datesLabel}
                </Text>
              </Flex>
            </Flex>

            <Box
              width="24px"
              height="24px"
              flexShrink="0"
              aria-hidden="true"
              sx={{
                transform: isDatesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <DropdownSmall width="24" height="24" fill={theme.colors.text.primary} />
            </Box>
          </Flex>
        </Box>

        {isDatesDropdownOpen && (
          <RevampDatesDropdown
            onClose={handleDatesDropdownClose}
            onSave={handleDatesSave}
            locale={locale}
            triggerRef={datesRowRef}
            startDate={hasDateRange ? selectedDate : undefined}
            endDate={hasDateRange ? getRangeEndDate(selectedDate, selectedEndDate) : undefined}
            dates={looseDates}
            isRecurring={isRecurring}
            weekDays={weekDays}
            allowsLooseDates={allowsLooseDates}
            allowsRecurring={allowsRecurring}
          />
        )}
      </Box>

      {isShowingBhBanner && (
        <Box pt="8x">
          <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
        </Box>
      )}

      <Box pt={isShowingBhBanner ? '0x' : '8x'} pb="6x">
        <ContactButton
          sitterInfo={sitterData}
          sitterSlug={getSitterSlugFromWebUrl(sitterData.webUrl)}
          contactUrl={contactUrl}
          isLoggedIn={currentUser.isAuthenticated}
          location={location}
          pagePosition="sidebar"
          shouldShowRevampLayout
          disabled={isContactUnavailable}
        />
      </Box>

      <RevampCancellationPolicy
        cancellationPolicy={cancellationPolicy}
        serviceName={selectedService?.displayName}
        cancellationPolicyDesc={selectedService?.meta?.cancellationPolicyDesc}
        providerId={sitterData.opk}
      />
    </Flex>
  );
};

export default RevampServicesCtaPriceDisplay;
